import { useEffect, useMemo, useState } from 'react';
import { Mail, MessageCircle, Reply, Send } from 'lucide-react';
import useApi from '../api';
import { formatDate } from '../utils/dateFormatter';
import SectionHeader from '@/components/ui/SectionHeader';

interface ProactiveNudge {
  sent_at?: string | null;
  content: string;
}

interface ProactiveConversation {
  conversation_id: string;
  channel?: string | null;
  replied: boolean;
  nudges?: ProactiveNudge[];
}

export default function ProactiveMessages({ leadId }: { leadId: string }) {
  const { get } = useApi();
  const [history, setHistory] = useState<ProactiveConversation[]>([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoaded(false);
    get(`/leads/${leadId}/proactive-messages`)
      .then((response) => {
        if (!cancelled) setHistory(response.data?.items || []);
      })
      .catch((error) => console.error('Error fetching proactive messages:', error))
      .finally(() => {
        if (!cancelled) setLoaded(true);
      });
    return () => { cancelled = true; };
  }, [get, leadId]);

  const messages = useMemo(
    () => history.flatMap((conversation) =>
      (conversation.nudges || []).map((nudge) => ({ ...nudge, ...conversation }))
    ),
    [history]
  );

  if (!loaded || !messages.length) return null;

  return (
    <div className="space-y-2">
      <SectionHeader
        title="Proactive messages"
        icon={Send}
        description="Messages the AI initiated before this contact asked for help."
      />
      <div className="divide-y rounded-xl border bg-card">
        {messages.map((message, index) => {
          const isEmail = message.channel === 'email';
          const ChannelIcon = isEmail ? Mail : MessageCircle;
          return (
            <div key={`${message.conversation_id}-${message.sent_at || index}`} className="px-4 py-3">
              <div className="mb-1.5 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                <span className="inline-flex items-center gap-1 capitalize">
                  <ChannelIcon className="h-3.5 w-3.5" /> {message.channel || 'widget'}
                </span>
                {message.sent_at && <span>{formatDate(message.sent_at, 'short')}</span>}
                <span className={`ml-auto inline-flex items-center gap-1 rounded-xl px-2 py-0.5 font-semibold ${
                  message.replied
                    ? 'bg-green-500/10 text-green-700 dark:text-green-400'
                    : 'bg-muted text-muted-foreground'
                }`}>
                  <Reply className="h-3 w-3" /> {message.replied ? 'Replied' : 'No reply'}
                </span>
              </div>
              <p className="whitespace-pre-wrap text-sm leading-relaxed">{message.content}</p>
            </div>
          );
        })}
      </div>
    </div>
  );
}
